import { PROVISIONAL_BELOW, TARGET_SIZES_MM, columnOf } from './trials';
import { wideGapLayout } from '../profile/layouts';
import type { CalibrationTrial, LayoutId, Settings } from '../types';

/**
 * The §5.6 inference rules: trials in, a proposal out.
 *
 * Pure, and the most consequential pure function in the app. A caregiver will act on what this
 * says — it is the substitute for a clinical assessment we cannot obtain — so every rule is tested
 * against synthetic trial sets whose answer is known by construction.
 *
 * Two things it deliberately will not do:
 *
 *  - **Auto-apply.** It returns a proposal; a human accepts it. An automatic layout change would be
 *    the app deciding something about a person's vision from 45 taps.
 *  - **Propose the impossible.** Where the right answer is a setting the app cannot honour (a key
 *    size above the frame's 19.5 mm ceiling) or a mode that does not exist yet (dwell, Phase 3), it
 *    says so in `recommendations` instead of writing a number that silently clamps or a setting
 *    that would leave the person unable to activate anything.
 */

/**
 * The largest key the frame can render, at any column count.
 *
 * Measured in Stage C: 320 pt of keyboard band over three rows caps key height at 19.5 mm, and
 * eleven columns cap width at 19.27 mm. §5.6's "set --key-mm to the smallest reliable size + 1
 * step" assumes the keyboard can grow to meet the person. Past this, it cannot.
 */
export const KEY_SIZE_CEILING_MM = 19.5;

/** Above this miss rate a size is not "reliable" for this person. */
const RELIABLE_MISS_RATE = 0.2;

/** Above this, at every size, direct touch is in question. */
const HIGH_MISS_RATE = 0.4;

/** A column this much worse than the best column suggests a field problem, not noise. */
const COLUMN_ASYMMETRY = 0.3;

/** Mean offset beyond this fraction of the target radius is a systematic bias, not scatter. */
const BIAS_FRACTION = 0.35;

/** Slow search everywhere. Deliberately generous: this person is not in a hurry. */
const SLOW_SEARCH_MS = 3000;

export interface CalibrationProposal {
  /** Settings the app can honour today. Empty is a valid result. */
  derivedSettings: Partial<Settings>;
  /**
   * Advice a `Partial<Settings>` cannot carry: things the app cannot do yet, and things a human
   * has to decide. German — a caregiver reads these.
   */
  recommendations: string[];
  /** Rests on fewer trials than the protocol; say so rather than implying confidence. */
  provisional: boolean;
  /** What the numbers were, so the proposal can be checked rather than trusted. */
  summary: {
    completed: number;
    missRateBySize: Array<{ targetMm: number; missRate: number }>;
    missRateByColumn: [number, number, number];
    neverTouched: number;
    meanOffset: { x: number; y: number };
    medianTimeToTouchMs: number | null;
    smallestReliableMm: number | null;
  };
}

const mean = (values: number[]): number =>
  values.length === 0 ? 0 : values.reduce((a, b) => a + b, 0) / values.length;

function median(values: number[]): number | null {
  if (values.length === 0) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0 ? ((sorted[mid - 1] ?? 0) + (sorted[mid] ?? 0)) / 2 : (sorted[mid] ?? 0);
}

function missRate(trials: CalibrationTrial[]): number {
  if (trials.length === 0) return 0;
  return trials.filter((t) => !t.hit).length / trials.length;
}

/**
 * `currentLayout` is only used to widen the gaps without moving the letters — the proposal is for the
 * ten-column version of the board the person already has, not for QWERTZ. It defaults to the app's
 * own default so that the rules stay testable from trials alone.
 */
export function inferFromTrials(
  trials: CalibrationTrial[],
  currentLayout: LayoutId = 'de-CH',
): CalibrationProposal {
  const derivedSettings: Partial<Settings> = {};
  const recommendations: string[] = [];

  const missRateBySize = TARGET_SIZES_MM.map((targetMm) => ({
    targetMm,
    missRate: missRate(trials.filter((t) => t.targetMm === targetMm)),
  })).filter(({ targetMm }) => trials.some((t) => t.targetMm === targetMm));

  const byColumn: [number, number, number] = [0, 1, 2].map((column) =>
    missRate(trials.filter((t) => columnOf(t.position) === column)),
  ) as [number, number, number];

  const touched = trials.filter((t) => t.offset !== null);
  const meanOffset = {
    x: mean(touched.map((t) => t.offset?.x ?? 0)),
    y: mean(touched.map((t) => t.offset?.y ?? 0)),
  };

  const medianTimeToTouchMs = median(
    trials.map((t) => t.timeToFirstTouchMs).filter((v): v is number => v !== null),
  );

  const neverTouched = trials.filter((t) => t.neverTouched).length;

  // Ascending by size, so the first reliable entry is the smallest size this person can hit.
  const ascending = [...missRateBySize].sort((a, b) => a.targetMm - b.targetMm);
  const smallestReliableMm =
    ascending.find(({ missRate: rate }) => rate <= RELIABLE_MISS_RATE)?.targetMm ?? null;

  const overallMiss = missRate(trials);
  // The miss rate at the LARGEST target. `missRateBySize` is ordered largest-first (the protocol
  // starts big), so `.at(-1)` would be the smallest — which made a person who hits 34 mm perfectly
  // look like a person who misses everything, and fired the "direct touch may not be viable" rule
  // on someone whose only problem was small targets.
  const largestSizeMiss = ascending.at(-1)?.missRate ?? 0;

  // ── Field loss / inattention ────────────────────────────────────────────────────────────────
  // One column much worse than the others, especially with targets never touched there. A
  // systematic side asymmetry is the signal; uniform difficulty is not.
  const worstColumn = byColumn.indexOf(Math.max(...byColumn)) as 0 | 1 | 2;
  const bestColumnRate = Math.min(...byColumn);
  const columnGap = (byColumn[worstColumn] ?? 0) - bestColumnRate;

  if (columnGap >= COLUMN_ASYMMETRY && worstColumn !== 1) {
    const side = worstColumn === 0 ? 'links' : 'rechts';
    derivedSettings.contentBandPct = 50;
    // Offset away from the weak side: positive moves the band right, away from a left-side problem.
    derivedSettings.contentBandOffsetPct = worstColumn === 0 ? 15 : -15;
    recommendations.push(
      `Treffer fehlen vor allem ${side}. Das Inhaltsband wird schmaler und von ${side} weg verschoben. ` +
        `Bitte in Phase 3 prüfen, ob ein halbseitiges Layout hilft.`,
    );
  }

  // ── Acuity ─────────────────────────────────────────────────────────────────────────────────
  // Misses fall away as the target grows: size is the binding constraint, not position.
  const sizeSensitive =
    missRateBySize.length >= 2 &&
    (ascending[0]?.missRate ?? 0) - largestSizeMiss >= 0.25 &&
    columnGap < COLUMN_ASYMMETRY;

  if (sizeSensitive) {
    derivedSettings.keyEchoEnabled = true;
    recommendations.push(
      'Kleine Ziele werden deutlich häufiger verfehlt. Der gesprochene Tastenecho wird eingeschaltet.',
    );

    // Note there is deliberately no "raise --key-mm" branch, and it is not an omission. §5.6's
    // ladder is 34/28/24/20/18 mm, and the frame's ceiling is 19.5 mm — so every rung that would
    // justify larger keys is already above what the keyboard can render. Someone reliable at 18 mm
    // is already served by the 19 mm default. The branch would be dead code that reads as a
    // safeguard.
    if (smallestReliableMm !== null && smallestReliableMm > KEY_SIZE_CEILING_MM) {
      // §5.6 would have us raise --key-mm here; the frame cannot, so saying "keys will be bigger"
      // would be a promise the layout breaks the moment it renders.
      derivedSettings.keyboardLayout = wideGapLayout(currentLayout);
      recommendations.push(
        `Zuverlässig getroffen werden erst Ziele ab ${smallestReliableMm} mm. Die Tastatur kann ` +
          `höchstens ${KEY_SIZE_CEILING_MM} mm grosse Tasten anzeigen — grösser geht nicht. ` +
          `Umgestellt wird auf das Layout mit weiten Abständen (ohne Komma, Punkt und Bindestrich). ` +
          `Verlässlicher sind Sätze, die Schnellwörter und Abkürzungen; Verweilen oder Scannen ` +
          `folgt in Phase 3.`,
      );
    }
  }

  // ── Systematic motor bias ──────────────────────────────────────────────────────────────────
  // Touches land consistently off-centre in one direction. Correct for it rather than asking the
  // person to compensate for something they cannot see themselves doing.
  const smallestTargetPx = (ascending[0]?.targetMm ?? 18) * 5.2;
  const biasThreshold = (smallestTargetPx / 2) * BIAS_FRACTION;
  const biased = Math.abs(meanOffset.x) > biasThreshold || Math.abs(meanOffset.y) > biasThreshold;

  if (biased && touched.length > 0) {
    derivedSettings.touchOffsetCorrection = {
      x: Math.round(-meanOffset.x),
      y: Math.round(-meanOffset.y),
    };
    derivedSettings.slopPx = 32;
    recommendations.push(
      'Berührungen liegen regelmässig in dieselbe Richtung neben der Mitte. Es wird eine ' +
        'Korrektur angewendet und die Toleranz erhöht.',
    );
  }

  // ── High visual search cost ────────────────────────────────────────────────────────────────
  if (medianTimeToTouchMs !== null && medianTimeToTouchMs > SLOW_SEARCH_MS) {
    // Phase 1 has no prediction row, so this is stored for Phase 2 rather than acted on now.
    derivedSettings.candidateCount = 3;
    recommendations.push(
      'Das Suchen der Ziele dauert lange. Vorschläge werden später auf drei begrenzt; ' +
        'Abkürzungen sind für diese Person wahrscheinlich hilfreicher als Wortvorhersage.',
    );
  }

  // ── Direct touch may not be viable ─────────────────────────────────────────────────────────
  // Deliberately last, and deliberately advice only: writing inputMode: 'dwell' would select an
  // adapter that does not exist until Phase 3 and leave the person unable to activate anything.
  if (trials.length > 0 && overallMiss >= HIGH_MISS_RATE && largestSizeMiss >= HIGH_MISS_RATE) {
    recommendations.push(
      `Auch grosse Ziele werden häufig verfehlt (${Math.round(overallMiss * 100)} % Fehltreffer). ` +
        'Direktes Antippen ist möglicherweise nicht der richtige Weg. Verweilen (Dwell) oder ' +
        'Scannen kommt in Phase 3 — bis dahin sind Sätze und Schnellwörter der verlässliche Pfad.',
    );
  }

  if (trials.length === 0) {
    recommendations.push('Keine Versuche aufgezeichnet. Bitte die Zielübung erneut starten.');
  } else if (recommendations.length === 0) {
    recommendations.push(
      'Keine Auffälligkeiten. Die aktuellen Einstellungen passen; die Übung kann jederzeit ' +
        'wiederholt werden.',
    );
  }

  return {
    derivedSettings,
    recommendations,
    provisional: trials.length < PROVISIONAL_BELOW,
    summary: {
      completed: trials.length,
      missRateBySize,
      missRateByColumn: byColumn,
      neverTouched,
      meanOffset,
      medianTimeToTouchMs,
      smallestReliableMm,
    },
  };
}
